// Der Inhaber schreibt einen oder mehrere Mandanten an.
//
// Zwei Schritte in einem Fenster: oben die Empfänger, unten die Nachricht.
// Die Empfänger stehen oben, weil sie den Ton bestimmen — eine Zeile an einen
// einzelnen Kunden schreibt man anders als ein Rundschreiben an alle.
//
// Zwei Dinge, die hier bewusst unbequem sind:
//   • Die Empfänger gehen immer als ausdrückliche Liste zum Server, nie als
//     Filtername. Was abgeschickt wird, ist genau das, was gezählt dasteht.
//   • Ab fünf Empfängern muss die Anzahl bestätigt werden. Ein Rundschreiben
//     lässt sich nicht zurückholen.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { datum } from '../../../gemeinsam/lib/daten';
import { type Anhang } from '../../firma/lib/dateien';
import { Abzeichen, Hinweis, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';
import { AnhangWahl } from '../../support/komponenten/SupportVerlauf';
import Icon from '../../../gemeinsam/komponenten/Icon';

type Mandant = {
  id: string; name: string; plan: string; mail: string; ansprechpartner: string;
  hatSepa: boolean; status: string;
  registriertAm: string | null; letzteAktivitaet: string | null;
  tageStill: number | null; tageDabei: number | null;
};
type Gruppe = { id: string; name: string; ids: string[] };
type Vorlage = {
  id: string; name: string; betreff: string; text: string; kategorie: string; eigene: boolean;
};
type Platzhalter = { name: string; erklaerung: string };

const KATEGORIEN: Array<[string, string]> = [
  ['sonstiges', 'Sonstiges'],
  ['frage', 'Frage'],
  ['abrechnung', 'Abrechnung'],
  ['wunsch', 'Wunsch'],
  ['fehler', 'Fehler'],
];

/**
 * Ab hier gilt es als Rundschreiben und will ausdrücklich bestätigt werden.
 * Eine oder zwei Nachrichten schreibt man gezielt; ab der dritten geht sie an
 * eine Gruppe, und eine Gruppe lässt sich nicht zurückrufen.
 */
const SCHWELLE_BESTAETIGUNG = 3;

/** Dieselben Namen wie auf dem Server — sonst steht in der Vorschau „starter". */
const TARIF_NAME: Record<string, string> = {
  starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise',
};

export default function MandantAnschreiben({ vorausgewaehlt = [], beiFertig, beiAbbruch }: {
  /** Mandanten-IDs, die beim Öffnen schon angehakt sind. */
  vorausgewaehlt?: string[];
  beiFertig: (meldung: string, anfrageId: string | null) => void;
  beiAbbruch: () => void;
}) {
  const [mandanten, setMandanten] = useState<Mandant[] | null>(null);
  const [gruppen, setGruppen] = useState<Gruppe[]>([]);
  const [vorlagen, setVorlagen] = useState<Vorlage[]>([]);
  const [platzhalter, setPlatzhalter] = useState<Platzhalter[]>([]);

  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set(vorausgewaehlt));
  const [suche, setSuche] = useState('');
  const [listeOffen, setListeOffen] = useState(vorausgewaehlt.length === 0);

  const [betreff, setBetreff] = useState('');
  const [inhalt, setInhalt] = useState('');
  const [kategorie, setKategorie] = useState('sonstiges');
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [vorschau, setVorschau] = useState(false);

  const [bestaetigt, setBestaetigt] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const [e, v] = await Promise.all([
        api<{ mandanten: Mandant[]; gruppen: Gruppe[] }>('/support/owner/empfaenger'),
        api<{ vorlagen: Vorlage[]; platzhalter: Platzhalter[] }>('/support/owner/vorlagen'),
      ]);
      setMandanten(e.mandanten);
      setGruppen(e.gruppen);
      setVorlagen(v.vorlagen);
      setPlatzhalter(v.platzhalter);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Mandanten ließen sich nicht laden.');
      setMandanten([]);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  const sichtbar = useMemo(() => {
    if (!mandanten) return [];
    const s = suche.trim().toLowerCase();
    if (!s) return mandanten;
    return mandanten.filter(m =>
      m.name.toLowerCase().includes(s)
      || m.ansprechpartner.toLowerCase().includes(s)
      || m.mail.toLowerCase().includes(s));
  }, [mandanten, suche]);

  const gewaehlteListe = useMemo(
    () => (mandanten ?? []).filter(m => gewaehlt.has(m.id)),
    [mandanten, gewaehlt],
  );

  const ohneMail = gewaehlteListe.filter(m => !m.mail);
  const rundschreiben = gewaehlt.size >= SCHWELLE_BESTAETIGUNG;
  const bereit = gewaehlt.size > 0 && betreff.trim() && inhalt.trim()
    && (!rundschreiben || bestaetigt);

  function umschalten(id: string) {
    setGewaehlt(g => {
      const neu = new Set(g);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      return neu;
    });
    setBestaetigt(false);
  }

  function gruppeSetzen(g: Gruppe) {
    setGewaehlt(new Set(g.ids));
    setBestaetigt(false);
  }

  function vorlageNehmen(v: Vorlage) {
    setBetreff(v.betreff);
    setInhalt(v.text);
    setKategorie(v.kategorie);
  }

  /** Platzhalter für die Vorschau einsetzen — dieselbe Regel wie auf dem Server. */
  function eingesetzt(text: string, m: Mandant | undefined) {
    if (!m) return text;
    const werte: Record<string, string> = {
      firma: m.name,
      name: m.ansprechpartner,
      vorname: m.ansprechpartner.split(' ')[0] ?? '',
      tarif: TARIF_NAME[m.plan] ?? m.plan,
    };
    let r = text;
    if (!werte.name) r = r.replace(/(Guten Tag|Hallo|Sehr geehrte[r]?)\s*\{(name|vorname)\}/gi, '$1');
    for (const [k, v] of Object.entries(werte)) r = r.replaceAll(`{${k}}`, v);
    return r.replace(/\{[a-zA-Z]+\}/g, '').replace(/ {2,}/g, ' ').replace(/ ([,.!?])/g, '$1').trim();
  }

  async function senden() {
    setSendet(true);
    setFehler(null);
    try {
      const r = await api<{
        angelegt: number; zugestellt: number; ohneMailadresse: string[];
        fehlgeschlagen: { tenantId: string; grund: string }[]; anfrageId: string | null;
      }>('/support/owner/neu', {
        methode: 'POST',
        daten: {
          tenantIds: [...gewaehlt], betreff, text: inhalt, kategorie,
          dateiIds: anhaenge.map(a => a.id),
        },
      });

      const teile = [`${r.angelegt} ${r.angelegt === 1 ? 'Nachricht' : 'Nachrichten'} angelegt`];
      if (r.zugestellt) teile.push(`${r.zugestellt} per Mail zugestellt`);
      if (r.ohneMailadresse.length) teile.push(`ohne Mailadresse: ${r.ohneMailadresse.join(', ')}`);
      if (r.fehlgeschlagen.length) teile.push(`${r.fehlgeschlagen.length} fehlgeschlagen`);
      beiFertig(teile.join(' · '), r.anfrageId);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das Versenden ist fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  const ersterEmpfaenger = gewaehlteListe[0];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex max-h-[95vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl dark:bg-slate-900">

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div>
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">Mandanten anschreiben</h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Es entsteht je Mandant ein eigenes Gespräch — niemand sieht die anderen Empfänger.
            </p>
          </div>
          <button onClick={beiAbbruch} aria-label="Schließen"
                  className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
            <Icon name="schliessen" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

          {/* Empfänger */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Empfänger{' '}
                <span className="font-normal text-slate-500">
                  ({gewaehlt.size} {gewaehlt.size === 1 ? 'Mandant' : 'Mandanten'})
                </span>
              </h3>
              <button type="button" onClick={() => setListeOffen(o => !o)}
                      className="rounded-lg px-2.5 py-1 text-xs font-medium text-marke-700 hover:bg-marke-50 dark:text-marke-300 dark:hover:bg-marke-950">
                {listeOffen ? 'Zuklappen' : 'Auswählen'}
              </button>
            </div>

            {!listeOffen && gewaehlteListe.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {gewaehlteListe.map(m => (
                  <span key={m.id} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs dark:border-slate-700 dark:bg-slate-800">
                    {m.name}
                    <button type="button" onClick={() => umschalten(m.id)}
                            aria-label={`${m.name} entfernen`}
                            className="text-slate-400 hover:text-red-600">×</button>
                  </span>
                ))}
              </div>
            )}

            {listeOffen && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800">
                <div className="flex flex-wrap gap-1.5 border-b border-slate-200 p-3 dark:border-slate-800">
                  {gruppen.map(g => (
                    <button key={g.id} type="button" onClick={() => gruppeSetzen(g)}
                            className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
                      {g.name} <span className="opacity-60">{g.ids.length}</span>
                    </button>
                  ))}
                  {gewaehlt.size > 0 && (
                    <button type="button" onClick={() => { setGewaehlt(new Set()); setBestaetigt(false); }}
                            className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                      Auswahl leeren
                    </button>
                  )}
                </div>

                <div className="border-b border-slate-200 p-3 dark:border-slate-800">
                  <input value={suche} onChange={e => setSuche(e.target.value)}
                         placeholder="Nach Firma, Person oder Mail suchen …"
                         className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
                </div>

                <ul className="max-h-64 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-800">
                  {mandanten === null ? (
                    <li className="flex justify-center py-8"><Spinner /></li>
                  ) : sichtbar.length === 0 ? (
                    <li className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                      {mandanten.length === 0 ? 'Noch keine Mandanten.' : 'Kein Treffer.'}
                    </li>
                  ) : sichtbar.map(m => (
                    <li key={m.id}>
                      <label className="flex cursor-pointer items-start gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <input type="checkbox" checked={gewaehlt.has(m.id)} onChange={() => umschalten(m.id)}
                               className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-marke-600" />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-slate-900 dark:text-slate-100">{m.name}</span>
                            <Abzeichen art={m.plan === 'enterprise' ? 'blau' : m.plan === 'professional' ? 'gruen' : 'neutral'}>
                              {m.plan}
                            </Abzeichen>
                            {!m.hatSepa && <Abzeichen art="gelb">kein SEPA</Abzeichen>}
                            {m.status === 'frozen' && <Abzeichen art="rot">eingefroren</Abzeichen>}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                            {m.ansprechpartner}
                            {m.mail ? ` · ${m.mail}` : ' · keine Mailadresse'}
                            {m.letzteAktivitaet
                              ? ` · zuletzt aktiv ${datum(m.letzteAktivitaet)}`
                              : ' · noch nichts gebucht'}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {ohneMail.length > 0 && (
              <div className="mt-2">
                <Hinweis art="warnung">
                  Ohne Mailadresse: {ohneMail.map(m => m.name).join(', ')}. Die Nachricht liegt
                  trotzdem in der App bereit, es geht nur keine Mail hinaus.
                </Hinweis>
              </div>
            )}
          </section>

          {/* Vorlagen */}
          {vorlagen.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Vorlage</h3>
              <div className="flex flex-wrap gap-1.5">
                {vorlagen.map(v => (
                  <button key={v.id} type="button" onClick={() => vorlageNehmen(v)}
                          className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-marke-300 hover:bg-marke-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-marke-800 dark:hover:bg-marke-950/30">
                    {v.name}{v.eigene && <span className="ms-1 opacity-60">eigene</span>}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Nachricht */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Nachricht</h3>

            <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Betreff</span>
                <input value={betreff} onChange={e => setBetreff(e.target.value)} maxLength={200}
                       className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Kategorie</span>
                <select value={kategorie} onChange={e => setKategorie(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
                  {KATEGORIEN.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Text</span>
              <textarea value={inhalt} onChange={e => setInhalt(e.target.value)} rows={10} maxLength={10000}
                        placeholder="Guten Tag {name}, …"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
            </label>

            <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <span>Platzhalter:</span>
              {platzhalter.map(p => (
                <button key={p.name} type="button" title={p.erklaerung}
                        onClick={() => setInhalt(t => `${t}{${p.name}}`)}
                        className="rounded border border-slate-300 px-1.5 py-0.5 font-mono hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800">
                  {`{${p.name}}`}
                </button>
              ))}
            </div>

            <AnhangWahl anhaenge={anhaenge} setAnhaenge={setAnhaenge} beschriftung="Anhängen" beiFehler={setFehler} />
          </section>

          {/* Vorschau */}
          {inhalt.trim() && ersterEmpfaenger && (
            <section>
              <button type="button" onClick={() => setVorschau(v => !v)}
                      className="text-sm font-medium text-marke-700 hover:underline dark:text-marke-300">
                {vorschau ? 'Vorschau ausblenden' : `Vorschau für ${ersterEmpfaenger.name}`}
              </button>
              {vorschau && (
                <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {eingesetzt(betreff, ersterEmpfaenger) || '(ohne Betreff)'}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                    {eingesetzt(inhalt, ersterEmpfaenger)}
                  </p>
                  {gewaehlt.size > 1 && (
                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                      Die übrigen {gewaehlt.size - 1} bekommen denselben Text mit ihren eigenen Angaben.
                    </p>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Bestätigung bei vielen Empfängern */}
          {rundschreiben && (
            <Hinweis art="warnung" titel={`Rundschreiben an ${gewaehlt.size} Mandanten`}>
              <label className="mt-1.5 flex cursor-pointer items-start gap-2">
                <input type="checkbox" checked={bestaetigt} onChange={e => setBestaetigt(e.target.checked)}
                       className="mt-0.5 h-4 w-4 rounded border-slate-300 text-marke-600" />
                <span>
                  Ja, an alle {gewaehlt.size} senden. Das lässt sich nicht zurücknehmen —
                  jeder bekommt die Nachricht in der App und per Mail.
                </span>
              </label>
            </Hinweis>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {gewaehlt.size === 0
              ? 'Noch kein Empfänger gewählt.'
              : `Geht an ${gewaehlt.size} ${gewaehlt.size === 1 ? 'Mandanten' : 'Mandanten'}.`}
          </p>
          <div className="flex gap-2">
            <Knopf onClick={beiAbbruch} disabled={sendet}>Abbrechen</Knopf>
            <Knopf variante="primaer" onClick={senden} laedt={sendet} disabled={!bereit}>
              {sendet ? 'Wird gesendet' : 'Senden'}
            </Knopf>
          </div>
        </div>
      </div>
    </div>
  );
}
